import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, sortBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import {
  Feature,
  FeatureNames,
  ScheduleCommands,
  ScheduleEntryProperty,
  ScheduleProperties,
} from '@vi-iot/client';

import { InstallationDetailsStore, Platform } from '@store';

import moment from 'moment';
import {
  DHW_CIRCULATION_SCHEDULE_LEVEL,
  DHW_SCHEDULE_LEVEL,
  HEATING_SCHEDULE_LEVEL,
  HYBRID_PRO_SCHEDULE_LEVEL,
  NOISE_REDUCTION_SCHEDULE_LEVEL,
  ROOM_SCHEDULE_LEVEL,
  SCHEDULE_TYPE,
  VENTILATION_SCHEDULE_LEVEL,
} from '../schedule.enums';

import { getDuration } from './helpers';
import { Program, ScheduleEntry } from './timemanager.models';

export interface Schedule {
  mon: ScheduleEntryProperty[];
  tue: ScheduleEntryProperty[];
  wed: ScheduleEntryProperty[];
  thu: ScheduleEntryProperty[];
  fri: ScheduleEntryProperty[];
  sat: ScheduleEntryProperty[];
  sun: ScheduleEntryProperty[];
}

@Injectable()
export class ScheduleService {
  heatingSuffix = 'Heating';
  programs$: BehaviorSubject<Program[]> = new BehaviorSubject<Program[]>([]);
  schedules$: BehaviorSubject<ScheduleEntry[][]> = new BehaviorSubject<ScheduleEntry[][]>([]);
  schedulesWithoutOverlap$: BehaviorSubject<ScheduleEntry[][]> = new BehaviorSubject<
    ScheduleEntry[][]
  >([]);
  isDhwSchedule$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isETPSchedule$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  currPrograms: Program[];
  currScheduleEntries: ScheduleEntry[][];

  // Schedule specification
  resolution: number;
  maxEntries: number;
  defaultMode: string;
  defaultProgram: string;
  scheduleType: string;

  platform: string;

  readonly daysKeys: string[];

  toastMessages: { [key: string]: string };
  days: string[];

  private allDayDuration = 1440;

  constructor(
    private translation: TranslateService,
    private installationDetailsStore: InstallationDetailsStore
  ) {
    this.initTranslatedVariables();

    this.daysKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

    this.installationDetailsStore
      .getMainDevicePlatform()
      .subscribe((platformType: Platform) => (this.platform = platformType));

    this.translation.onLangChange.subscribe(() => {
      this.initTranslatedVariables();
    });
  }

  setCurrPrograms(programs: Program[]): void {
    this.currPrograms = programs;
  }

  getScheduleEntry(
    feature: Feature<ScheduleProperties, ScheduleCommands>,
    day: string
  ): ScheduleEntry[] {
    const entries = feature?.properties?.entries?.value;
    if (entries && entries[day]) {
      return this.sortScheduleEntry(entries[day]);
    }

    return [];
  }

  sortScheduleEntry(entry: ScheduleEntry[]): ScheduleEntry[] {
    return (
      entry
        // Calculate duration for start hour
        .map((program) => ({
          ...program,
          duration: getDuration('00:00', program.start),
        }))

        // Sort programs by duration
        .sort((a, b) => {
          if (a.duration < b.duration) {
            return -1;
          }
          if (a.duration > b.duration) {
            return 1;
          }

          return 0;
        })

        // Return sorted programs
        .map((program, i) => ({
          start: program.start,
          end: program.end,
          mode: program.mode,
          position: i,
        }))
    );
  }

  getPrograms(programs: Program[]): Program[] {
    if (programs && programs.length) {
      programs = this.stylePrograms(programs);

      return programs;
    }

    return [{ name: 'default', css: 'program-default' }];
  }

  getDays(): string[] {
    return this.days;
  }

  calcDuration(start: string, end: string): number {
    const splitStart = start.split(':');
    const startHour = +splitStart[0];
    const startMin = +splitStart[1];

    const splitEnd = end.split(':');
    const endHour = +splitEnd[0];
    const endMin = +splitEnd[1];

    let hours = endHour - startHour;
    let mins = endMin - startMin;
    mins = startMin !== 0 ? mins + 60 : mins;
    hours = startMin !== 0 ? hours - 1 : hours;

    return hours * 60 + mins;
  }

  calcScheduleEntries(entry: ScheduleEntry[], avoidTimeOverlapping = false): ScheduleEntry[] {
    let allDayMode: string = null;
    let nextScheduleEntry: ScheduleEntry;
    const scheduleEntries: ScheduleEntry[] = [];
    const sortedScheduleEntries: ScheduleEntry[] = sortBy(entry, (d) => d.position).slice(
      0,
      this.maxEntries
    );

    if (sortedScheduleEntries.length) {
      const scheduleEntry = sortedScheduleEntries[0];
      if (scheduleEntry.start !== '00:00') {
        scheduleEntries.push({
          mode: this.defaultMode,
          start: '00:00',
          end: scheduleEntry.start,
          duration: getDuration('00:00', scheduleEntry.start),
        });
      }
    } else {
      scheduleEntries.push({
        position: 0,
        mode: this.defaultMode,
        start: '00:00',
        end: '24:00',
        duration: getDuration('00:00', '24:00'),
      });

      return scheduleEntries;
    }

    sortedScheduleEntries.forEach((scheduleEntry, i) => {
      nextScheduleEntry = sortedScheduleEntries[i + 1];
      // VIG-5651
      // if next entry overlaps current one we need to adapt start and end dates to avoid conflicts
      if (
        avoidTimeOverlapping &&
        nextScheduleEntry &&
        this.isScheduleTimeOverlapped(nextScheduleEntry.start, scheduleEntry.end)
      ) {
        if (getDuration(scheduleEntry.start, scheduleEntry.end) === this.allDayDuration) {
          allDayMode = scheduleEntry.mode;
        }
        const duration = getDuration(scheduleEntry.start, nextScheduleEntry.start);
        if (duration > 0) {
          scheduleEntries.push({
            mode: scheduleEntry.mode,
            start: scheduleEntry.start,
            end: nextScheduleEntry.start,
            duration,
          });
        }
      } else {
        scheduleEntries.push({
          ...scheduleEntry,
          duration: getDuration(scheduleEntry.start, scheduleEntry.end),
        });
      }

      if (nextScheduleEntry && scheduleEntry.end !== nextScheduleEntry.start) {
        const duration = getDuration(scheduleEntry.end, nextScheduleEntry.start);
        if (duration > 0) {
          scheduleEntries.push({
            mode: avoidTimeOverlapping && allDayMode ? allDayMode : this.defaultMode,
            start: scheduleEntry.end,
            end: nextScheduleEntry.start,
            duration,
          });
        }
      } else if (!nextScheduleEntry && scheduleEntry.end !== '24:00') {
        const duration = getDuration(scheduleEntry.end, '24:00');
        if (duration > 0) {
          scheduleEntries.push({
            mode: avoidTimeOverlapping && allDayMode ? allDayMode : this.defaultMode,
            start: scheduleEntry.end,
            end: '24:00',
            duration,
          });
        }
      }
    });

    scheduleEntries.forEach((scheduleEntry, i) => {
      scheduleEntry.position = i;
    });

    return scheduleEntries;
  }

  resetSchedules(): void {
    this.schedules$.next([]);
    this.schedulesWithoutOverlap$.next([]);
    this.programs$.next([]);
  }

  createSchedule(feature: Feature<ScheduleProperties, ScheduleCommands>): void {
    const schedules = [];
    const schedulesWithoutOverlap = [];
    this.resolution = feature?.commands?.setSchedule?.params?.newSchedule?.constraints?.resolution;
    this.maxEntries = feature?.commands?.setSchedule?.params?.newSchedule?.constraints?.maxEntries;
    this.defaultMode =
      feature?.commands?.setSchedule?.params?.newSchedule?.constraints?.defaultMode;
    this.defaultProgram = this.getDefaultProgram(feature);
    this.daysKeys.forEach((key) => {
      schedules.push(this.calcScheduleEntries(this.getScheduleEntry(feature, key)));
      schedulesWithoutOverlap.push(
        this.calcScheduleEntries(this.getScheduleEntry(feature, key), true)
      );
    });
    this.schedules$.next(schedules);
    this.schedulesWithoutOverlap$.next(schedulesWithoutOverlap);
  }

  createModes(modes: { name: string }[]): void {
    const flavoredModes = this.getPrograms(modes);
    this.setCurrPrograms(flavoredModes);
    this.programs$.next(flavoredModes);
  }

  updateScheduleEntryPeriods(dayIds: number[], scheduleEntries: ScheduleEntry[]): void {
    const schedules = this.schedules$.getValue();
    dayIds.forEach((dayId) => {
      schedules[dayId] = cloneDeep(scheduleEntries);
    });
    this.schedules$.next([...schedules]);
  }

  saveCurrScheduleEntries(): void {
    const schedules = this.schedules$.getValue();
    this.currScheduleEntries = cloneDeep(schedules);
  }

  getScheduleValue(
    dayIds: number[],
    scheduleEntries: ScheduleEntry[],
    schedule: Schedule
  ): Schedule {
    return this.updateSchedule(schedule, dayIds, scheduleEntries);
  }

  updateSchedule(schedule: Schedule, dayIds: number[], scheduleEntries: ScheduleEntry[]): Schedule {
    dayIds.forEach((dayId) => {
      schedule[this.daysKeys[dayId]] = scheduleEntries;
      schedule[this.daysKeys[dayId]] = schedule[this.daysKeys[dayId]].filter(
        (entry) => entry.mode !== this.defaultMode
      );
      schedule[this.daysKeys[dayId]].forEach((v, id) => {
        v.position = id;
        delete v.duration;
      });
    });

    return schedule;
  }

  stylePrograms(programs: Program[]): Program[] {
    programs.forEach((program) => {
      program.css = 'program-' + program.name.replace(/\//gm, '-');
    });

    return programs;
  }

  getScheduleEntries(
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): {
    mon: ScheduleEntryProperty[];
    tue: ScheduleEntryProperty[];
    wed: ScheduleEntryProperty[];
    thu: ScheduleEntryProperty[];
    fri: ScheduleEntryProperty[];
    sat: ScheduleEntryProperty[];
    sun: ScheduleEntryProperty[];
  } {
    const scheduleFeature = cloneDeep(feature);

    return (
      scheduleFeature?.properties?.entries?.value || {
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
        sun: [],
      }
    );
  }

  supportHeatingPrograms(programs: Program[]): Program[] {
    return [
      ...programs.map((program) => ({
        ...program,
        name: program.name + this.heatingSuffix,
      })),
      ...programs,
    ];
  }

  public getDefaultProgram(feature: Feature<ScheduleProperties, ScheduleCommands>): string {
    this.scheduleType = this.getScheduleType(feature);

    switch (this.scheduleType) {
      case SCHEDULE_TYPE.NoiseReductionSchedule:
        return NOISE_REDUCTION_SCHEDULE_LEVEL.NotReduced;
      case SCHEDULE_TYPE.HeatingSchedule:
        return HEATING_SCHEDULE_LEVEL.Normal;
      case SCHEDULE_TYPE.DhwSchedule:
        return this.platform === Platform.Legacy
          ? DHW_SCHEDULE_LEVEL.Normal
          : DHW_SCHEDULE_LEVEL.On;
      case SCHEDULE_TYPE.DhwCirculationSchedule:
        return this.platform === Platform.Legacy
          ? DHW_CIRCULATION_SCHEDULE_LEVEL.On5minEvery25min
          : DHW_CIRCULATION_SCHEDULE_LEVEL.On;
      case SCHEDULE_TYPE.RoomSchedule:
        return ROOM_SCHEDULE_LEVEL.Normal;
      case SCHEDULE_TYPE.VentilationSchedule:
        return feature?.commands?.setSchedule?.params?.newSchedule?.constraints?.modes.includes(
          VENTILATION_SCHEDULE_LEVEL.LevelThree
        )
          ? VENTILATION_SCHEDULE_LEVEL.LevelThree
          : VENTILATION_SCHEDULE_LEVEL.Standard;
      case SCHEDULE_TYPE.HybridPro:
        return HYBRID_PRO_SCHEDULE_LEVEL.Normal;
      default:
        return feature?.commands?.setSchedule?.params?.newSchedule?.constraints?.defaultMode;
    }
  }

  private getScheduleType(feature: Feature<ScheduleProperties, ScheduleCommands>): string {
    const featureName = feature.feature ? feature.feature.replace(/[0-9]/g, '') : '';

    if (featureName === 'heating.circuits..heating.schedule') {
      return SCHEDULE_TYPE.HeatingSchedule;
    } else if (featureName === FeatureNames.heatingNoiseReductionSchedule) {
      return SCHEDULE_TYPE.NoiseReductionSchedule;
    } else if (
      featureName === FeatureNames.dhwSchedule ||
      featureName === 'heating.circuits..dhw.schedule'
    ) {
      return SCHEDULE_TYPE.DhwSchedule;
    } else if (
      featureName === FeatureNames.dhwPumpsCirculationSchedule ||
      featureName === 'heating.circuits..circulation.schedule'
    ) {
      return SCHEDULE_TYPE.DhwCirculationSchedule;
    } else if (featureName === 'rooms.others..schedule' || featureName === 'rooms..schedule') {
      return SCHEDULE_TYPE.RoomSchedule;
    } else if (feature.feature === FeatureNames.ventilationSchedule) {
      return SCHEDULE_TYPE.VentilationSchedule;
    } else if (featureName === FeatureNames.secondaryHeatGeneratorElecticityPriceSchedule) {
      return SCHEDULE_TYPE.HybridPro;
    } else {
      return null;
    }
  }

  private isScheduleTimeOverlapped(beginningTime: string, endTime: string): boolean {
    return moment(beginningTime, 'hh:mm').isBefore(moment(endTime, 'hh:mm'));
  }

  private initTranslatedVariables(): void {
    this.days = [
      this.translation.instant('+SCHEDULES.DAY.MONDAY'),
      this.translation.instant('+SCHEDULES.DAY.TUESDAY'),
      this.translation.instant('+SCHEDULES.DAY.WEDNESDAY'),
      this.translation.instant('+SCHEDULES.DAY.THURSDAY'),
      this.translation.instant('+SCHEDULES.DAY.FRIDAY'),
      this.translation.instant('+SCHEDULES.DAY.SATURDAY'),
      this.translation.instant('+SCHEDULES.DAY.SUNDAY'),
    ];

    this.toastMessages = {
      scheduleSuccess: this.translation.instant('+SCHEDULES.SNACKBAR.SCHEDULE_SUCCESS'),
      scheduleFailure: this.translation.instant('+SCHEDULES.SNACKBAR.SCHEDULE_FAILURE'),
      scheduleEntrySuccess: this.translation.instant('+SCHEDULES.SNACKBAR.PROGRAM_SUCCESS'),
      scheduleEntryFailure: this.translation.instant('+SCHEDULES.SNACKBAR.PROGRAM_FAILURE'),
      activateProgramSuccessSuccess: this.translation.instant(
        '+SCHEDULES.SNACKBAR.ACTIVATE_PROGRAM_SUCCESS'
      ),
      activateProgramSuccessFailure: this.translation.instant(
        '+SCHEDULES.SNACKBAR.ACTIVATE_PROGRAM_FAILURE'
      ),
      deactivateProgramSuccessSuccess: this.translation.instant(
        '+SCHEDULES.SNACKBAR.DEACTIVATE_PROGRAM_SUCCESS'
      ),
      deactivateProgramSuccessFailure: this.translation.instant(
        '+SCHEDULES.SNACKBAR.DEACTIVATE_PROGRAM_FAILURE'
      ),
    };
  }
}
